import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { GlobalTradeSwitch } from "@/components/admin/GlobalTradeSwitch";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";

const MetricCard = ({ title, value, testId }) => (
  <article className="rounded border border-slate-700 bg-slate-900 p-4" data-testid={`${testId}-card`}>
    <p className="text-xs uppercase tracking-wider text-slate-500" data-testid={`${testId}-label`}>{title}</p>
    <p className="mt-2 text-2xl font-black text-white" data-testid={`${testId}-value`}>{value ?? "-"}</p>
  </article>
);

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [actionAudit, setActionAudit] = useState([]);
  const [incidentHistory, setIncidentHistory] = useState([]);
  const [loadError, setLoadError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [summaryRes, auditRes, incidentRes] = await Promise.allSettled([
        apiClient.get("/dashboard/summary", { timeout: 12000 }),
        apiClient.get("/admin/live-trading/control-layer/action-audit", { params: { since_hours: 48, limit: 12 }, timeout: 12000 }),
        apiClient.get("/admin/action-center/incident-history", { params: { limit: 12 }, timeout: 12000 }),
      ]);

      if (summaryRes.status === "fulfilled") {
        setSummary(summaryRes.value?.data || null);
      }
      if (auditRes.status === "fulfilled") {
        setActionAudit(auditRes.value?.data?.items || []);
      }
      if (incidentRes.status === "fulfilled") {
        const items = incidentRes.value?.data?.audit_events || incidentRes.value?.data?.recent_alerts || [];
        setIncidentHistory(items);
      }

      const failed = [summaryRes, auditRes, incidentRes].filter((item) => item.status === "rejected").length;
      if (failed === 3) {
        setLoadError("Gösterge paneli verisi alınamadı");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return <LoadingSkeleton rows={8} testId="admin-dashboard-loading-skeleton" />;
  }

  const metrics = summary || {};

  return (
    <section className="space-y-6" data-testid="admin-dashboard-page">
      <header className="rounded border border-slate-700 bg-slate-950 p-5" data-testid="admin-dashboard-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="admin-dashboard-kicker">Admin Control</p>
            <h1 className="text-4xl font-black uppercase tracking-tight text-white" data-testid="admin-dashboard-title">Observability + Kill Switch</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400" data-testid="admin-dashboard-subtitle">
              Admin tarafı yalnız sistem görünürlüğü ve acil kapatma kontrolü için kullanılır. Trade, queue veya kullanıcı adına execution aksiyonu yoktur.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-dashboard-header-actions">
            <Button variant="outline" onClick={() => navigate("/admin/action-audit")} data-testid="admin-dashboard-open-action-audit-button">Action Audit</Button>
            <Button onClick={loadDashboard} data-testid="admin-dashboard-refresh-button">Yenile</Button>
          </div>
        </div>
      </header>

      <section className="rounded border border-red-700/60 bg-red-950/20 p-5" data-testid="admin-dashboard-kill-switch-panel">
        <p className="text-xs uppercase tracking-[0.2em] text-red-300" data-testid="admin-dashboard-kill-switch-title">Emergency Kill Switch</p>
        <p className="mt-2 text-sm text-slate-300" data-testid="admin-dashboard-kill-switch-description">
          Sistem içindeki tek intentional blok kaynağı kill switch’tir. Açıkken akış durur, kapalıyken user tarafı kendi akışıyla çalışır.
        </p>
        <div className="mt-4" data-testid="admin-dashboard-kill-switch-control-wrap">
          <GlobalTradeSwitch />
        </div>
      </section>

      {loadError && (
        <div className="rounded border border-amber-700 bg-amber-950/20 p-4 text-sm text-amber-200" data-testid="admin-dashboard-load-error-banner">
          {loadError}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="admin-dashboard-metrics-grid">
        <MetricCard title="Kullanıcı" value={metrics.users ?? "-"} testId="admin-dashboard-users" />
        <MetricCard title="Running Bot" value={metrics.running_bots ?? "-"} testId="admin-dashboard-running-bots" />
        <MetricCard title="Signal / 5m" value={metrics.signals_5m ?? "-"} testId="admin-dashboard-signals" />
        <MetricCard title="Open Positions" value={metrics.open_positions ?? "-"} testId="admin-dashboard-open-positions" />
      </section>

      <section className="grid gap-4 xl:grid-cols-2" data-testid="admin-dashboard-readonly-panels-grid">
        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-dashboard-action-audit-panel">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-300" data-testid="admin-dashboard-action-audit-title">Action Audit</p>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/action-audit")} data-testid="admin-dashboard-action-audit-open-page-button">Detay</Button>
          </div>
          <div className="mt-3 space-y-2" data-testid="admin-dashboard-action-audit-list">
            {actionAudit.length === 0 && <p className="text-sm text-slate-500" data-testid="admin-dashboard-action-audit-empty">Kayıt yok.</p>}
            {actionAudit.map((item, index) => (
              <article key={`${item.id}-${index}`} className="rounded border border-slate-800 p-3 text-xs" data-testid={`admin-dashboard-action-audit-item-${index}`}>
                <p className="font-semibold text-white" data-testid={`admin-dashboard-action-audit-item-action-${index}`}>{item.action}</p>
                <p className="mt-1 text-slate-400" data-testid={`admin-dashboard-action-audit-item-meta-${index}`}>{item.created_at} · {item.actor_role}</p>
              </article>
            ))}
          </div>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-dashboard-incident-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-300" data-testid="admin-dashboard-incident-title">Incident / Akış Geçmişi</p>
          <div className="mt-3 space-y-2" data-testid="admin-dashboard-incident-list">
            {incidentHistory.length === 0 && <p className="text-sm text-slate-500" data-testid="admin-dashboard-incident-empty">Kayıt yok.</p>}
            {incidentHistory.map((item, index) => (
              <article key={`${item.id || index}`} className="rounded border border-slate-800 p-3 text-xs" data-testid={`admin-dashboard-incident-item-${index}`}>
                <p className="font-semibold text-white" data-testid={`admin-dashboard-incident-item-title-${index}`}>{item.action || item.alert_type || item.root_cause_code || "event"}</p>
                <p className="mt-1 text-slate-400" data-testid={`admin-dashboard-incident-item-meta-${index}`}>{item.created_at || item.updated_at || "-"}</p>
              </article>
            ))}
          </div>
        </article>
      </section>
    </section>
  );
};